<template>
  <div class="rounded-lg border border-neutralLight">
    <!-- Header -->
    <div class="flex items-center justify-between px-4 py-3 rounded-t-lg bg-[#F0F0F4]">
      <RuleBuilderOperator
        :model-value="group.operator"
        :options="config.operators"
        :disabled="isOperatorDisabled"
        :label="operatorLabel"
        @update:model-value="(val) => emit('update:group', { ...group, operator: val })"
      />
      <div class="flex items-center gap-2">
        <outlined-button
          v-if="depth < resolvedMaxDepth"
          :text="config.addGroupLabel ?? 'Novo grupo'"
          width="112px"
          height="32px"
          @click="addGroup"
        />
        <filled-button
          :text="config.addRuleLabel ?? 'Nova regra'"
          width="135px"
          height="34px"
          class="bg-primary30"
          :prepend-icon="mdiPlus"
          @click="addRule"
        />
        <v-btn
          v-if="depth > 0"
          icon
          variant="text"
          size="small"
          density="compact"
          data-testid="group-btn-remove"
          @click="emit('remove')"
        >
          <v-icon :icon="mdiClose" size="20" class="text-gray-400" />
        </v-btn>
      </div>
    </div>

    <!-- Children -->
    <div ref="childrenContainerRef" class="p-4">
      <draggable
        :model-value="group.children"
        item-key="_index"
        handle=".drag-handle"
        ghost-class="opacity-30"
        :animation="200"
        :disabled="!(config.allowDragDrop ?? true)"
        @update:model-value="handleReorder"
      >
        <template #item="{ element, index }">
          <div class="mb-2 last:mb-0">
            <!-- Rule row -->
            <div
              v-if="element.type === 'rule'"
              class="flex items-center gap-3 px-3 h-16 rounded-lg border border-neutralLight bg-[#F0F0F4]"
            >
              <v-icon
                :icon="mdiDragVertical"
                class="drag-handle cursor-grab text-gray-400"
                size="20"
              />

              <span class="text-sm font-bold text-primary50 whitespace-nowrap">
                {{ resolvedRuleLabel(getRuleNumber(index)) }}
              </span>

              <div>
                <slot
                  name="rule"
                  :rule="element"
                  :index="index"
                  :update="(val: unknown) => updateRule(index, val)"
                  :remove="() => removeChild(index)"
                  :removable="config.isRuleRemovable ? config.isRuleRemovable(element) : true"
                />
              </div>

              <v-btn
                v-if="!config.isRuleRemovable || config.isRuleRemovable(element)"
                icon
                variant="text"
                size="small"
                density="compact"
                class="ml-auto"
                data-testid="rule-btn-remove"
                @click="removeChild(index)"
              >
                <v-icon :icon="mdiClose" size="24" class="text-textPrimary" />
              </v-btn>
            </div>

            <!-- Nested group -->
            <RuleBuilderGroup
              v-else
              :group="element"
              :config="config"
              :depth="depth + 1"
              :operator-label="operatorLabel"
              @update:group="(updated) => updateChild(index, updated)"
              @remove="removeChild(index)"
            >
              <template #rule="slotProps">
                <slot name="rule" v-bind="slotProps" />
              </template>
            </RuleBuilderGroup>
          </div>
        </template>
      </draggable>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref } from 'vue'
import draggable from 'vuedraggable'
import { mdiDragVertical, mdiClose, mdiPlus } from '@mdi/js'

import RuleBuilderOperator from './RuleBuilderOperator.vue'
import OutlinedButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'

import type {
  RuleBuilderGroup as RuleBuilderGroupType,
  RuleBuilderChild,
  RuleBuilderConfig
} from './types'

interface Props {
  group: RuleBuilderGroupType<string, unknown>
  config: RuleBuilderConfig<string>
  depth?: number
  operatorLabel?: string
}

const props = withDefaults(defineProps<Props>(), {
  depth: 0,
  operatorLabel: 'Operador'
})

const childrenContainerRef = ref<HTMLElement | null>(null)

const resolvedMaxDepth = props.config.maxDepth ?? 3

// Poucos filhos para haver o que combinar, ou o consumidor travou este nível.
const isOperatorDisabled = computed(
  () =>
    props.group.children.length < (props.config.minChildrenForOperator ?? 2) ||
    (props.config.isOperatorLocked?.(props.depth) ?? false)
)

function resolvedRuleLabel(ruleNumber: number): string {
  return props.config.ruleLabel?.(ruleNumber) ?? `Regra ${ruleNumber}`
}

/* eslint-disable sonarjs/no-duplicate-string */
const emit = defineEmits<{
  (e: 'update:group', group: RuleBuilderGroupType<string, unknown>): void
  (e: 'remove'): void
}>()

async function scrollToNewChild() {
  await nextTick()
  const container = childrenContainerRef.value
  if (!container) return
  const draggableRoot = container.firstElementChild as HTMLElement | null
  if (!draggableRoot) return
  const lastChild = draggableRoot.lastElementChild as HTMLElement | null
  if (!lastChild) return
  lastChild.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  lastChild.classList.add('rule-builder-item-highlight')
  lastChild.addEventListener(
    'animationend',
    () => lastChild.classList.remove('rule-builder-item-highlight'),
    { once: true }
  )
}

function addRule() {
  emit('update:group', {
    ...props.group,
    children: [...props.group.children, { type: 'rule' as const, value: null }]
  })
  scrollToNewChild()
}

function addGroup() {
  emit('update:group', {
    ...props.group,
    children: [
      ...props.group.children,
      {
        type: 'group' as const,
        operator: props.config.defaultOperator,
        children: [{ type: 'rule' as const, value: null }]
      }
    ]
  })
  scrollToNewChild()
}

async function removeChild(index: number) {
  const child = props.group.children[index]
  const callback =
    child.type === 'rule' ? props.config.beforeRemoveRule : props.config.beforeRemoveGroup

  if (callback) {
    const allowed = await callback(child)
    if (!allowed) return
  }

  emit('update:group', {
    ...props.group,
    children: props.group.children.filter((_, i) => i !== index)
  })
}

function updateChild(index: number, updated: RuleBuilderChild<string, unknown>) {
  const newChildren = [...props.group.children]
  newChildren[index] = updated
  emit('update:group', { ...props.group, children: newChildren })
}

function updateRule(index: number, value: unknown) {
  const child = props.group.children[index]
  if (child.type === 'rule') {
    updateChild(index, { ...child, value })
  }
}

function getRuleNumber(childIndex: number): number {
  let count = 1
  for (let i = 0; i < childIndex; i++) {
    if (props.group.children[i].type === 'rule') count++
  }
  return count
}

function handleReorder(newChildren: RuleBuilderChild<string, unknown>[]) {
  emit('update:group', { ...props.group, children: newChildren })
}
</script>

<style>
@keyframes rule-builder-highlight-fade {
  0% {
    background-color: #d8eef3;
  }
  100% {
    background-color: transparent;
  }
}

.rule-builder-item-highlight {
  animation: rule-builder-highlight-fade 1.5s ease-out;
  border-radius: 0.5rem;
}
</style>
